import React, { useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../contexts/ProjectContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { ProjectAlert, AlertType } from '../../types';
import { BellIcon } from '../icons/Icons';
import {
  AlertTriangle, Clock, AlarmClock, CalendarClock,
  UserCheck, MessageSquare, CheckCircle2, X,
} from 'lucide-react';
import './NotificationDropdown.css';

// ── helpers ──────────────────────────────────────────────────────────────────

type Group = 'critical' | 'warning' | 'info';

const ALERT_GROUP: Record<AlertType, Group> = {
  ISSUE_REPORTED:  'critical',
  TASK_OVERDUE:    'critical',
  LATE_COMPLETION: 'warning',
  TASK_DUE_SOON:   'warning',
  TASK_ASSIGNED:   'info',
  NEW_COMMENT:     'info',
};

const GROUP_META: Record<Group, { label: string; color: string }> = {
  critical: { label: 'Critical', color: '#ef4444' },
  warning:  { label: 'Warnings', color: '#f59e0b' },
  info:     { label: 'Info',     color: '#3b82f6' },
};

const ALERT_COLOR: Record<AlertType, string> = {
  ISSUE_REPORTED:  '#ef4444',
  TASK_OVERDUE:    '#ef4444',
  LATE_COMPLETION: '#f59e0b',
  TASK_DUE_SOON:   '#f59e0b',
  TASK_ASSIGNED:   '#3b82f6',
  NEW_COMMENT:     '#3b82f6',
};

function AlertTypeIcon({ type, size = 15 }: { type: AlertType; size?: number }) {
  const color = ALERT_COLOR[type];
  switch (type) {
    case 'ISSUE_REPORTED':  return <AlertTriangle  size={size} color={color} />;
    case 'TASK_OVERDUE':    return <Clock          size={size} color={color} />;
    case 'LATE_COMPLETION': return <AlarmClock     size={size} color={color} />;
    case 'TASK_DUE_SOON':   return <CalendarClock  size={size} color={color} />;
    case 'TASK_ASSIGNED':   return <UserCheck      size={size} color={color} />;
    case 'NEW_COMMENT':     return <MessageSquare  size={size} color={color} />;
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ── component ─────────────────────────────────────────────────────────────────

interface Props {
  onOpenAll?: () => void;
}

export const NotificationDropdown: React.FC<Props> = ({ onOpenAll }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { currentUser, isProjectManager } = useAuth();
  const { alerts, markAlertRead, markAllAlertsRead } = useProjects();

  useClickOutside(wrapperRef, () => setIsOpen(false), isOpen);

  const visible = useMemo(() => {
    if (isProjectManager) return alerts;
    if (!currentUser) return [];
    return alerts.filter(a => a.assignedTo.includes(currentUser.id));
  }, [alerts, isProjectManager, currentUser]);

  const grouped = useMemo(() => {
    const result: Record<Group, ProjectAlert[]> = { critical: [], warning: [], info: [] };
    visible.forEach(a => {
      const g = ALERT_GROUP[a.type] ?? 'info';
      result[g].push(a);
    });
    return result;
  }, [visible]);

  const visibleUnread = useMemo(
    () => visible.filter(a => !a.isRead).length,
    [visible]
  );

  const handleClick = useCallback((alert: ProjectAlert) => {
    markAlertRead(alert.id);
    setIsOpen(false);
    navigate(isProjectManager ? `/workspace/${alert.subElementId}` : `/my-tasks/${alert.subElementId}`);
  }, [markAlertRead, navigate, isProjectManager]);

  const handleDismiss = useCallback((e: React.MouseEvent, alertId: string) => {
    e.stopPropagation();
    markAlertRead(alertId);
  }, [markAlertRead]);

  const handleMarkAll = useCallback(() => {
    markAllAlertsRead();
  }, [markAllAlertsRead]);

  const handleViewAll = useCallback(() => {
    setIsOpen(false);
    if (onOpenAll) onOpenAll();
    navigate('/notifications');
  }, [navigate, onOpenAll]);

  const ORDER: Group[] = ['critical', 'warning', 'info'];

  return (
    <div className="notif-wrapper" ref={wrapperRef}>
      <button
        className={`notif-trigger${isOpen ? ' active' : ''}`}
        onClick={() => setIsOpen(v => !v)}
        title="Notifications"
        type="button"
      >
        <BellIcon size={22} />
        {visibleUnread > 0 && (
          <span className="notif-badge">{visibleUnread > 99 ? '99+' : visibleUnread}</span>
        )}
      </button>

      {isOpen && (
        <div className="notif-dropdown">
          {/* Header */}
          <div className="notif-header">
            <span className="notif-header-title">
              Notifications
              {visibleUnread > 0 && <span className="notif-header-count">{visibleUnread}</span>}
            </span>
            <div className="notif-header-actions">
              {visibleUnread > 0 && (
                <button className="notif-mark-all" onClick={handleMarkAll} type="button">
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Groups */}
          <div className="notif-body">
            {visible.length === 0 ? (
              <div className="notif-empty">
                <span className="notif-empty-icon">
                  <CheckCircle2 size={28} color="#22c55e" />
                </span>
                <p>You're all caught up</p>
              </div>
            ) : (
              ORDER.map(group => {
                const items = grouped[group];
                if (!items.length) return null;
                const meta = GROUP_META[group];
                return (
                  <div key={group} className={`notif-group notif-group-${group}`}>
                    <p className="notif-group-label">
                      <span className="notif-group-dot" style={{ background: meta.color }} />
                      {meta.label}
                      <span className="notif-group-count">{items.length}</span>
                    </p>
                    {items.slice(0, 6).map(alert => (
                      <div
                        key={alert.id}
                        className={`notif-item${!alert.isRead ? ' unread' : ''}`}
                        onClick={() => handleClick(alert)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && handleClick(alert)}
                      >
                        {!alert.isRead && <span className="notif-unread-dot" />}
                        <span className="notif-item-icon">
                          <AlertTypeIcon type={alert.type} />
                        </span>
                        <div className="notif-item-body">
                          <span className="notif-item-code">{alert.taskCode}</span>
                          <span className="notif-item-msg">{alert.message}</span>
                          <span className="notif-item-time">{timeAgo(alert.createdAt)}</span>
                        </div>
                        <button
                          className="notif-dismiss"
                          type="button"
                          onClick={e => handleDismiss(e, alert.id)}
                          title="Dismiss"
                          tabIndex={-1}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {visible.length > 0 && (
            <div className="notif-footer">
              <button className="notif-view-all" type="button" onClick={handleViewAll}>
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
